import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { scheduleApi, smsTemplateApi } from '../api';
import {
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS,
  SCHEDULE_STATUSES, SCHEDULE_STATUS_COLORS,
} from '../constants';

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type TabKey = 'all' | 'shooting' | 'select';

// URL path → 기본 탭 매핑. 메뉴 분할 라우트 대응.
// /schedules/consultation 라우트는 하위 호환으로 route는 유지하되 탭은 제거됨.
// 해당 URL 접근 시 null → 기본 'shooting'으로 fallback.
const pathToTab = (pathname: string): TabKey | null => {
  if (pathname.endsWith('/schedules/shooting')) return 'shooting';
  if (pathname.endsWith('/schedules/select')) return 'select';
  return null;
};

export default function PhotoStudioSchedules() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pathTab = pathToTab(location.pathname);
  const initialTab: TabKey = pathTab || (searchParams.get('tab') as TabKey) || 'shooting';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const isSubRoute = pathTab !== null;

  // URL 변화 시 탭 동기화
  useEffect(() => {
    if (pathTab) setActiveTab(pathTab);
  }, [pathTab]);

  const changeTab = (tab: TabKey) => {
    setActiveTab(tab);
    if (!isSubRoute) setSearchParams({ tab });
  };

  // === Single data source ===
  const [schedules, setSchedules] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calDate, setCalDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));
  const [categoryFilter, setCategoryFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkSmsModal, setShowBulkSmsModal] = useState(false);
  const [bulkSmsContext, setBulkSmsContext] = useState<any>(null); // { schedule, customer, staffList, recipients: [{to, name, photoCustomerIdx?, checked}] }
  const [bulkSmsText, setBulkSmsText] = useState('');
  const [bulkSmsTitle, setBulkSmsTitle] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  // 단체 발송 모달에서 템플릿 선택을 지원하기 위해 SMS 템플릿 목록을 한 번 로드.
  const [smsTemplates, setSmsTemplates] = useState<any[]>([]);
  useEffect(() => {
    smsTemplateApi.list({ size: 100 }).then((data: any) => {
      setSmsTemplates(data?.items || []);
    }).catch(() => {});
  }, []);

  // 템플릿 적용: 내용에 변수(#{신랑이름} 등)를 현재 일정의 고객 데이터로 치환.
  // 고객 없는 일정(자체 업무)이면 변수는 그대로 남음.
  const applyTemplateToBulk = (template: any) => {
    const customer = bulkSmsContext?.customer;
    let content = template.content || '';
    if (customer) {
      const fmt = (v?: string) => v ? new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
      content = content
        .replace(/#\{신랑이름\}/g, customer.groomName || '')
        .replace(/#\{신부이름\}/g, customer.brideName || '')
        .replace(/#\{촬영일\}/g, fmt(customer.mainShootingDt || customer.rehearsalDt))
        .replace(/#\{리허설일\}/g, fmt(customer.rehearsalDt))
        .replace(/#\{셀렉일\}/g, fmt(customer.selectDt))
        .replace(/#\{예식일\}/g, fmt(customer.weddingDt));
    }
    setBulkSmsText(content);
    if (template.title) setBulkSmsTitle(template.title);
  };
  const [addForm, setAddForm] = useState({
    section: 'SHOOTING', scheduleCategory: 'REHEARSAL_SHOOTING',
    title: '', startDt: '', endDt: '', customerName: '', photographerName: '', memo: '',
  });

  const year = calDate.getFullYear();
  const month = calDate.getMonth();

  const fetchSchedules = useCallback(async () => {
    setIsLoading(true);
    try {
      const lastDay = new Date(year, month + 1, 0);
      const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
      const data = await scheduleApi.list({ dateFrom, dateTo, size: 999 });
      setSchedules(data.items || data || []);
    } catch (err) {
      console.error('일정 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // 촬영자 목록 (필터용)
  const staffList = useMemo(() => {
    const names = new Set<string>();
    schedules.forEach((s: any) => {
      if (s.photographerName) names.add(s.photographerName);
      if (s.rehearsalPhotographerName) names.add(s.rehearsalPhotographerName);
      if (s.mainPhotographerName) names.add(s.mainPhotographerName);
      if (s.assignedStaff?.name) names.add(s.assignedStaff.name);
    });
    return Array.from(names).sort();
  }, [schedules]);

  // Filter schedules by active tab
  const filteredSchedules = useMemo(() => {
    let filtered = schedules;
    if (activeTab === 'shooting') {
      filtered = filtered.filter((s: any) => s.section === 'SHOOTING');
    } else if (activeTab === 'select') {
      filtered = filtered.filter((s: any) => s.section === 'SELECT');
    }
    if (activeTab === 'all' && categoryFilter) {
      filtered = filtered.filter((s: any) => s.scheduleCategory === categoryFilter);
    }
    if (staffFilter) {
      filtered = filtered.filter((s: any) =>
        s.photographerName === staffFilter ||
        s.rehearsalPhotographerName === staffFilter ||
        s.mainPhotographerName === staffFilter ||
        s.assignedStaff?.name === staffFilter
      );
    }
    return filtered;
  }, [schedules, activeTab, categoryFilter, staffFilter]);

  // Group schedules by date for calendar rendering
  const schedulesByDate = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const s of filteredSchedules) {
      const dateKey = s.startDt?.slice(0, 10);
      if (!dateKey) continue;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(s);
    }
    return map;
  }, [filteredSchedules]);

  // Build calendar weeks
  const calendarWeeks = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const cells: (number | null)[] = [];

    // Leading empty cells
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
    // Day cells
    for (let d = 1; d <= lastDate; d++) cells.push(d);
    // Trailing empty cells to complete the last week
    while (cells.length % 7 !== 0) cells.push(null);

    // Split into weeks
    const weeks: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }, [year, month]);

  const weekCount = calendarWeeks.length;
  const cellHeightPx = weekCount <= 4 ? 140 : weekCount <= 5 ? 116 : 96;

  const todayStr = toDateStr(new Date());

  const formatTime = (dt?: string) => {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  };

  // 고객 연결 일정인지 판별 (촬영/셀렉/예식 = 고객 연결)
  const isCustomerLinkedSchedule = (s: any) => {
    return s.photoCustomerIdx && ['REHEARSAL_SHOOTING', 'MAIN_SHOOTING', 'SELECT', 'WEDDING'].includes(s.scheduleCategory);
  };

  const renderScheduleCard = (s: any) => {
    const catInfo = SCHEDULE_CATEGORIES.find(c => c.value === s.scheduleCategory);
    const isLinked = isCustomerLinkedSchedule(s);
    return (
      <div key={s.photoScheduleIdx}
        onClick={() => isLinked && navigate(`/photo-studio/customer-detail?id=${s.photoCustomerIdx}`)}
        className={`flex gap-3 py-3 px-1 border-b border-gray-100 last:border-0 transition-colors ${
          isLinked ? 'hover:bg-blue-50/50 cursor-pointer' : 'hover:bg-gray-50/50'
        }`}>
        <div className={`w-1 flex-shrink-0 rounded-full mt-0.5 ${catInfo?.color || 'bg-blue-500'}`} style={{ minHeight: '40px' }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory] || 'bg-blue-100 text-blue-700'}`}>
              {catInfo?.label || '-'}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${SCHEDULE_STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-600'}`}>{s.status}</span>
            {isLinked && <span className="text-[10px] text-blue-400"><i className="ri-link mr-0.5"></i>고객연결</span>}
          </div>
          <div className="text-sm font-bold text-gray-900 mt-1 truncate">{s.title}</div>
          <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-2.5">
            <span><i className="ri-time-line mr-0.5"></i>{formatTime(s.startDt)} ~ {formatTime(s.endDt)}</span>
            {s.customerName && <span><i className="ri-user-3-line mr-0.5"></i>{s.customerName}</span>}
            {s.photographerName && <span><i className="ri-camera-line mr-0.5"></i>{s.photographerName}</span>}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); openBulkSms(s); }}
          title="관련자 전체 발송"
          className="self-center px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50 whitespace-nowrap"
        >
          <i className="ri-message-3-line mr-0.5"></i>발송
        </button>
        {isLinked && <i className="ri-arrow-right-s-line text-gray-300 self-center"></i>}
      </div>
    );
  };

  // === 단체 SMS 발송 ===
  const openBulkSms = async (schedule: any) => {
    const token = localStorage.getItem('accessToken');
    // 고객 정보 로드 (photoCustomerIdx 있을 때만)
    let customer: any = null;
    if (schedule.photoCustomerIdx) {
      try {
        const res = await fetch(`https://api.wedmin.biz/photo-studio/customer/${schedule.photoCustomerIdx}/detail?tab=OVERVIEW`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await res.json();
        customer = json?.data?.customer || json?.customer || null;
      } catch {}
    }
    // 업체 유저 목록 (촬영자/담당자 phone 매칭용)
    let staff: any[] = [];
    try {
      const res = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=200', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await res.json();
      staff = json?.data?.items || json?.items || [];
    } catch {}

    const lookupPhone = (userIdx?: number) => {
      if (!userIdx) return '';
      return staff.find((u: any) => u.userIdx === userIdx)?.phoneNumber || '';
    };

    const recipients: Array<{ to: string; name: string; role: string; photoCustomerIdx?: number; checked: boolean }> = [];
    if (customer) {
      if (customer.groomPhone)
        recipients.push({ to: customer.groomPhone, name: customer.groomName || '신랑', role: '신랑', photoCustomerIdx: customer.photoCustomerIdx, checked: true });
      if (customer.bridePhone)
        recipients.push({ to: customer.bridePhone, name: customer.brideName || '신부', role: '신부', photoCustomerIdx: customer.photoCustomerIdx, checked: true });
      if (customer.partnerStaffPhone)
        recipients.push({ to: customer.partnerStaffPhone, name: customer.partnerStaffName || '업체담당자', role: '업체담당자', checked: false });
      const addStaff = (idx: number | undefined, role: string) => {
        const phone = lookupPhone(idx);
        const name = staff.find((u: any) => u.userIdx === idx)?.name || role;
        if (phone) recipients.push({ to: phone, name, role, checked: true });
      };
      addStaff(customer.rehearsalPhotographerIdx, '리허설촬영1');
      addStaff(customer.rehearsalPhotographer2Idx, '리허설촬영2');
      addStaff(customer.mainPhotographerIdx, '본식촬영1');
      addStaff(customer.mainPhotographer2Idx, '본식촬영2');
      addStaff(customer.selectManagerIdx, '셀렉담당자');
      addStaff(customer.assignedStaffIdx, '담당직원');
    } else if (schedule.assignedStaffIdx) {
      // 고객 연결 없는 일정은 담당 직원만
      const phone = lookupPhone(schedule.assignedStaffIdx);
      const name = staff.find((u: any) => u.userIdx === schedule.assignedStaffIdx)?.name || '담당자';
      if (phone) recipients.push({ to: phone, name, role: '담당직원', checked: true });
    }

    // 중복 제거 (같은 phone)
    const seen = new Set<string>();
    const dedup = recipients.filter(r => {
      if (seen.has(r.to)) return false;
      seen.add(r.to);
      return true;
    });

    setBulkSmsContext({ schedule, customer, recipients: dedup });
    setBulkSmsTitle(schedule.title || '');
    setBulkSmsText(
      `[${schedule.title || '일정 안내'}]\n${schedule.startDt ? new Date(schedule.startDt).toLocaleString('ko-KR') : ''}${schedule.venue ? `\n장소: ${schedule.venue}` : ''}`
    );
    setShowBulkSmsModal(true);
  };

  const toggleRecipient = (index: number) => {
    setBulkSmsContext((prev: any) => {
      if (!prev) return prev;
      const next = [...prev.recipients];
      next[index] = { ...next[index], checked: !next[index].checked };
      return { ...prev, recipients: next };
    });
  };

  const handleSendBulkSms = async () => {
    if (!bulkSmsContext) return;
    const targets = bulkSmsContext.recipients.filter((r: any) => r.checked && r.to);
    if (targets.length === 0) { alert('수신자를 1명 이상 선택해주세요.'); return; }
    if (!bulkSmsText.trim()) { alert('발송 내용을 입력해주세요.'); return; }
    setBulkSending(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('https://api.wedmin.biz/photo-studio/sms/bulk', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: bulkSmsTitle,
          text: bulkSmsText,
          notificationType: 'GENERAL',
          recipients: targets.map((r: any) => ({
            to: r.to,
            name: r.name,
            photoCustomerIdx: r.photoCustomerIdx,
          })),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || '발송 실패');
      alert(`발송 완료: 성공 ${json.successCount}건 / 실패 ${json.failedCount}건`);
      setShowBulkSmsModal(false);
    } catch (err: any) {
      alert(`발송 실패: ${err.message}`);
    } finally {
      setBulkSending(false);
    }
  };

  const SECTION_MAP: Record<string, string> = {
    TASK: 'TASK', REHEARSAL_SHOOTING: 'SHOOTING', MAIN_SHOOTING: 'SHOOTING',
    SELECT: 'SELECT', CONSULTATION: 'CONSULTATION', WEDDING: 'SHOOTING',
    PAYMENT: 'TASK', DELIVERY: 'TASK',
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.title || !addForm.startDt) { alert('제목과 시작일은 필수입니다.'); return; }
    try {
      await scheduleApi.create({
        ...addForm,
        section: SECTION_MAP[addForm.scheduleCategory] || 'TASK',
        endDt: addForm.endDt || addForm.startDt,
      });
      alert('일정이 등록되었습니다.');
      setShowAddModal(false);
      setAddForm({ section: 'SHOOTING', scheduleCategory: 'REHEARSAL_SHOOTING', title: '', startDt: '', endDt: '', customerName: '', photographerName: '', memo: '' });
      fetchSchedules();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // Selected date schedules
  const selectedDateSchedules = useMemo(() => {
    return schedulesByDate[selectedDate] || [];
  }, [schedulesByDate, selectedDate]);

  const tabLabel =
    activeTab === 'all' ? '전체일정'
    : activeTab === 'shooting' ? '본식일정'
    : '셀렉일정';

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">일정관리</h1>
            <p className="text-sm text-gray-600 mt-1">촬영일정, 셀렉일정, 전체일정을 관리합니다</p>
          </div>
          <button onClick={() => { setAddForm(f => ({ ...f, startDt: selectedDate ? `${selectedDate}T09:00` : '' })); setShowAddModal(true); }}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm font-medium cursor-pointer shadow-sm">
            <i className="ri-add-line text-base"></i> 일정 등록
          </button>
        </div>

        {/* 탭 */}
        <div className="mb-6">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" role="tablist">
            {([
              { key: 'all' as TabKey, label: '전체일정', icon: 'ri-calendar-line', desc: '모든 일정을 한눈에' },
              { key: 'shooting' as TabKey, label: '본식일정', icon: 'ri-camera-line', desc: '리허설 · 본식 · 야외촬영' },
              { key: 'select' as TabKey, label: '셀렉일정', icon: 'ri-image-line', desc: '사진 셀렉 스케줄' },
            ]).map(tab => (
              <button key={tab.key} role="tab" aria-selected={activeTab === tab.key} onClick={() => changeTab(tab.key)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                  activeTab === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}>
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-2 ml-1">
            {activeTab === 'all' && '촬영·셀렉·예식 등 모든 유형의 일정을 통합 조회합니다'}
            {activeTab === 'shooting' && '리허설촬영, 본식촬영, 야외촬영 등 촬영 관련 스케줄을 관리합니다'}
            {activeTab === 'select' && '촬영 후 고객과 진행하는 사진 셀렉 일정을 관리합니다'}
          </p>
        </div>

        {/* 필터 영역 */}
        <div className="mb-4 flex items-center gap-4 flex-wrap">
          {/* 카테고리 필터 (전체일정 탭에서만) */}
          {activeTab === 'all' && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={() => setCategoryFilter('')}
                className={`text-[11px] px-2.5 py-1 rounded-md cursor-pointer transition-colors ${
                  !categoryFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}>전체</button>
              {SCHEDULE_CATEGORIES.map(cat => (
                <button key={cat.value} onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
                  className={`text-[11px] px-2.5 py-1 rounded-md cursor-pointer transition-colors ${
                    categoryFilter === cat.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}>{cat.label}</button>
              ))}
            </div>
          )}
          {/* 촬영자 필터 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">촬영자:</span>
            <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}
              className="h-8 px-2.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">전체</option>
              {staffList.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 캘린더 + 상세 패널 */}
        <div className="grid grid-cols-12 gap-6">
        <div className="col-span-9 bg-white rounded-xl border border-gray-200 p-6">
          {/* 월 네비게이션 */}
          <div className="flex justify-between items-center mb-5">
            <button
              onClick={() => setCalDate(new Date(year, month - 1))}
              aria-label="이전 달"
              className="p-2.5 hover:bg-gray-100 rounded-lg cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
            </button>
            <span className="text-xl font-bold text-gray-800">{year}년 {month + 1}월</span>
            <button
              onClick={() => setCalDate(new Date(year, month + 1))}
              aria-label="다음 달"
              className="p-2.5 hover:bg-gray-100 rounded-lg cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center p-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* 요일 헤더 */}
              <div className="grid grid-cols-7 border-b border-gray-200">
                {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                  <div
                    key={d}
                    className={`py-2.5 text-center text-sm font-semibold ${
                      i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'
                    }`}
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* 캘린더 그리드 */}
              <div className="grid grid-cols-7">
                {calendarWeeks.map((week, wi) =>
                  week.map((day, di) => {
                    if (day === null) {
                      return (
                        <div
                          key={`${wi}-${di}`}
                          style={{ height: cellHeightPx }}
                          className={`border-b border-r border-gray-100 bg-gray-50/30 ${
                            di === 0 ? 'border-l border-gray-100' : ''
                          }`}
                        />
                      );
                    }

                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayOfWeek = new Date(year, month, day).getDay();
                    const isSunday = dayOfWeek === 0;
                    const isSaturday = dayOfWeek === 6;
                    const isToday = dateStr === todayStr;
                    const isSelected = dateStr === selectedDate;
                    const daySchedules = schedulesByDate[dateStr] || [];
                    const maxVisible = 2;
                    const visibleSchedules = daySchedules.slice(0, maxVisible);
                    const remainingCount = daySchedules.length - maxVisible;

                    return (
                      <button
                        key={`${wi}-${di}`}
                        type="button"
                        onClick={() => setSelectedDate(dateStr)}
                        aria-label={`${month + 1}월 ${day}일`}
                        aria-pressed={isSelected}
                        style={{ height: cellHeightPx }}
                        className={`flex flex-col items-stretch justify-start border-b border-r border-gray-100 p-1.5 text-left cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset overflow-hidden ${
                          di === 0 ? 'border-l border-gray-100' : ''
                        } ${
                          isSelected
                            ? 'bg-blue-50 ring-2 ring-inset ring-blue-400'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* 날짜 번호 */}
                        <div className="flex items-start justify-start mb-1">
                          {isToday ? (
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">
                              {day}
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center justify-center w-7 h-7 text-xs font-medium ${
                                isSunday ? 'text-red-500' : isSaturday ? 'text-blue-500' : 'text-gray-700'
                              }`}
                            >
                              {day}
                            </span>
                          )}
                        </div>

                        {/* 일정 목록 */}
                        <div className="space-y-0.5">
                          {visibleSchedules.map((s: any) => {
                            const catInfo = SCHEDULE_CATEGORIES.find(c => c.value === s.scheduleCategory);
                            return (
                              <div
                                key={s.photoScheduleIdx}
                                className="flex items-center gap-1 overflow-hidden"
                              >
                                <div className={`w-0.5 h-3.5 flex-shrink-0 rounded-full ${catInfo?.color || 'bg-blue-500'}`} />
                                <span className="text-[10px] text-gray-700 truncate leading-tight">
                                  {s.title || s.customerName || '-'}
                                </span>
                              </div>
                            );
                          })}
                          {remainingCount > 0 && (
                            <div className="text-[10px] text-blue-500 font-medium pl-2">
                              +{remainingCount}건 더보기
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* 선택한 날짜 상세 패널 (우측) */}
        <div className="col-span-3 bg-white rounded-xl border border-gray-200 p-5 self-start sticky top-8">
          {selectedDate ? (
            <>
              <h3 className="font-semibold text-gray-800 mb-3 text-base">
                {selectedDate.slice(5).replace('-', '월 ')}일 {tabLabel}
                <span className="ml-1.5 text-sm font-normal text-gray-400">
                  ({selectedDateSchedules.length}건)
                </span>
              </h3>
              {selectedDateSchedules.length > 0 ? (
                <div className="space-y-1">
                  {selectedDateSchedules.map((s: any) => renderScheduleCard(s))}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  <i
                    className={`${
                      activeTab === 'shooting' ? 'ri-camera-off-line' : activeTab === 'select' ? 'ri-image-line' : 'ri-calendar-line'
                    } text-3xl block mb-2 text-gray-300`}
                    aria-hidden="true"
                  ></i>
                  <p className="text-sm">일정이 없습니다</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <i className="ri-calendar-line text-3xl block mb-2 text-gray-300"></i>
              <p className="text-sm">날짜를 선택해주세요</p>
            </div>
          )}
        </div>
        </div>

        {/* 일정 등록 모달 */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">일정 등록</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
              </div>
              <form onSubmit={handleCreateSchedule} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">일정 유형 *</label>
                    <select value={addForm.scheduleCategory} onChange={(e) => setAddForm({ ...addForm, scheduleCategory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                    <input type="text" value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} required
                      placeholder="예: 김홍기 리허설촬영"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">시작일시 *</label>
                    <input type="datetime-local" value={addForm.startDt} onChange={(e) => setAddForm({ ...addForm, startDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">종료일시</label>
                    <input type="datetime-local" value={addForm.endDt} onChange={(e) => setAddForm({ ...addForm, endDt: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                    <input type="text" value={addForm.customerName} onChange={(e) => setAddForm({ ...addForm, customerName: e.target.value })}
                      placeholder="고객명"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">촬영자</label>
                    <input type="text" value={addForm.photographerName} onChange={(e) => setAddForm({ ...addForm, photographerName: e.target.value })}
                      placeholder="촬영자명"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={addForm.memo} onChange={(e) => setAddForm({ ...addForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                  <button type="button" onClick={() => setShowAddModal(false)}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer text-sm">취소</button>
                  <button type="submit"
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer text-sm">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 단체 SMS 발송 모달 */}
        {showBulkSmsModal && bulkSmsContext && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  <i className="ri-message-3-line mr-2 text-blue-500"></i>관련자 단체 발송
                </h2>
                <button onClick={() => setShowBulkSmsModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="text-sm text-gray-500">
                  일정: <span className="text-gray-900 font-medium">{bulkSmsContext.schedule?.title}</span>
                </div>

                {/* 수신자 리스트 */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">수신자 선택</div>
                  {bulkSmsContext.recipients.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400 border border-dashed rounded-lg">
                      이 일정에 연결된 연락처가 없습니다. 고객/촬영자/담당자 정보를 확인해주세요.
                    </div>
                  ) : (
                    <div className="space-y-1.5 border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
                      {bulkSmsContext.recipients.map((r: any, i: number) => (
                        <label key={`${r.to}-${i}`} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-gray-50 px-1 rounded">
                          <input type="checkbox" checked={r.checked} onChange={() => toggleRecipient(i)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 w-20 text-center">{r.role}</span>
                          <span className="text-sm font-medium text-gray-900">{r.name}</span>
                          <span className="text-sm text-gray-500 ml-auto">{r.to}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* 템플릿 불러오기 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">템플릿 불러오기</label>
                  {smsTemplates.length > 0 ? (
                    <div className="flex gap-1.5 flex-wrap">
                      {smsTemplates.map((t: any) => (
                        <button key={t.photoSmsTemplateIdx} type="button" onClick={() => applyTemplateToBulk(t)}
                          className="text-xs px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer">
                          {t.templateName}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">등록된 템플릿이 없습니다. 고객 상세 &gt; 문자전송 탭 &gt; 템플릿 관리에서 추가할 수 있습니다.</p>
                  )}
                </div>

                {/* 제목 / 내용 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제목 <span className="text-gray-400 text-xs">(로그 기록용)</span></label>
                  <input type="text" value={bulkSmsTitle} onChange={(e) => setBulkSmsTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">발송 내용</label>
                  <textarea value={bulkSmsText} onChange={(e) => setBulkSmsText(e.target.value)} rows={6}
                    placeholder="수신자 전원에게 동일 내용이 발송됩니다."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  <div className="text-xs text-gray-400 mt-1">{bulkSmsText.length} 자</div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button onClick={() => setShowBulkSmsModal(false)} disabled={bulkSending}
                    className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer text-sm disabled:opacity-50">취소</button>
                  <button onClick={handleSendBulkSms} disabled={bulkSending || bulkSmsContext.recipients.filter((r: any) => r.checked).length === 0}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                    {bulkSending ? '발송중...' : `${bulkSmsContext.recipients.filter((r: any) => r.checked).length}명 발송`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
